import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_CUSTOMERS } from "../../../utils/constants";
import customerService from "../../../services/ticketService/customerService";
import { CustomerInterface } from "../../../models/TicketModule/Customer";

interface DeleteCustomerContext {
  previousCustomers: CustomerInterface[];
  deletedCustomer: CustomerInterface | undefined;
}

const useDeleteCustomer = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    void, // No return data after deletion
    Error,
    string, // ID of the customer to delete
    DeleteCustomerContext
  >({
    mutationFn: customerService.Remove, // Remove should take a string ID

    onMutate: (customerId: string) => {
      const previousCustomers =
        queryClient.getQueryData<CustomerInterface[]>([
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      // Optimistically update the cache
      queryClient.setQueryData<CustomerInterface[]>(
        [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        previousCustomers.filter((customer) => customer.id !== customerId) // Use statusId to filter
      );

      return {
        previousCustomers,
        deletedCustomer: previousCustomers.find(
          (customer) => customer.id === customerId
        ),
      };
    },

    onError: (error, customerId, context) => {
      if (!context) return;

      // Rollback to the previous state in case of error
      queryClient.setQueryData<CustomerInterface[]>(
        [
          CACHE_KEY_CUSTOMERS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousCustomers
      );
    },

    onSuccess: () => {
      // Optionally, you could perform additional actions on success
      // For example, invalidating queries or updating other parts of the state
    },
  });
};

export default useDeleteCustomer;
